import axios from 'axios';
import { i18n } from '@/lang/i18n';
import { environmentService, postRequestHandler, getRequestHandler } from '@/utils/services';


const TARGET_HOTEL_CLIENT = 3,
tenantTargetIndexes = [0, 4],

DefaultState = ()=>{
  return {
    emails:[],
    booking:null,
    apiUrl:null,
    apiUrlBooking:null,
    hotelContacts:[],
    clientContacts: [],
    failedEmail: {},
    hostConfiguration: null,
    sentDone: false,
    targets: ['All', 'Hotel', 'Client', 'Hotel & Client', 'Tenant'],
    targetValues: ['all', 'hotel', 'client', 'hotel_client', 'tenant'],
    selectedTarget: 0,
    showQv: false,
    refreshTarget: false,
    contactForm: {}
  }
},

state = DefaultState(),

getters = {
  emails: state => state.emails,
  booking: state => state.booking,
  hotelContacts: state => state.hotelContacts,
  clientContacts: state => state.clientContacts,
  failedEmail: state => state.failedEmail,
  hostConfiguration: state => state.hostConfiguration,
  sentDone: state => state.sentDone,
  targets: state => state.targets,
  targetValues: state => state.targetValues,
  // Without a tenant configuration the tenant mail cannot be sent, so 'All' (index 0) and 'Tenant' (index 4) are hidden.
  availableTargets: state => state.targets
    .map((label, index) => ({label, index}))
    .filter(target => state.hostConfiguration || !tenantTargetIndexes.includes(target.index)),
  showQv: state => state.showQv,
  refreshTarget: state => state.refreshTarget,
  selectedTarget: state => state.selectedTarget,
  contactForm: state => state.contactForm
},

mutations = {
  setUrls(state) {
    state.apiUrlBooking = environmentService.apiUrlBooking;
    state.apiUrl = environmentService.apiUrlMails;
  },

  setMails(state, payload) {
    state.emails = payload;
  },

  removeEmail(state, id) {
    state.emails = state.emails.filter(email => email.id != id);
  },

  setBooking(state, payload) {
    state.booking = payload
  },

  setFailedEmail(state, payload) {
    state.failedEmail = payload;
  },

  setHostConfiguration(state, payload) {
    state.hostConfiguration = payload;
  },

  setContacts(state, payload) {
    state.hotelContacts = payload.hotelContacts;
    state.clientContacts = payload.clientContacts;
  },

  cancelResend(state) {
    state.booking = null;
    state.clientContacts = [];
    state.hotelContacts = [];
    state.failedEmail = {};
    state.hostConfiguration = null;
  },

  toggleNotify(state, payload) {
    if (payload.target == 'hotel') {
      const userIndex = state.hotelContacts.findIndex(contact => contact.email == payload.email);
      if (userIndex >= 0) {
        state.hotelContacts[userIndex].notify = !state.hotelContacts[userIndex].notify;
      }
    } else {
      const userIndex = state.clientContacts.findIndex(contact => contact.email == payload.email);
      if (userIndex >= 0) {
        state.clientContacts[userIndex].notify = !state.clientContacts[userIndex].notify;
      }
    }
  },

  addRecipient(state, payload) {
    const record = {
      first_name: payload.first_name,
      last_name: payload.last_name,
      email: payload.email,
      notify: payload.notify
    }

    if (payload.target == 'Hotel') {
      state.hotelContacts.push(record);
    } else {
      state.clientContacts.push(record);
    }
  },

  setSelectedTarget(state, payload) {
    state.selectedTarget = payload;
  },

  toggleQv(state, payload) {
    state.showQv = payload;
  },

  toggleSentDone(state, payload) {
    state.sentDone = payload;
  },

  updateTarget(state) {
    state.refreshTarget =  !state.refreshTarget;
  }
},

actions = {
  setUrls({commit}) {
    commit('setUrls');
  },

  updateTarget({ commit }) {
    commit('updateTarget');
  },

  toggleQv({ commit }, payload) {
    commit('toggleQv', payload);
  },

  toggleSentDone({commit}, payload) {
    commit('toggleSentDone', payload);
  },

  async getFailedMails({commit, state, dispatch, rootState}) {
    if (!state.apiUrl) {
      dispatch('setUrls');
    }
    await getRequestHandler(`${state.apiUrl}/bookings/failed`, {headers: rootState.auth.headers})
      .then(response => {
        commit('setMails', response);
      })
      .catch(error => console.log(error));
  },

  async requestDeleteFailedEmail({commit, dispatch, state, rootState}, payload) {
    if (!state.apiUrl) {
      dispatch('setUrls');
    }
    i18n.locale = rootState.global.locale;
    try {
      const response = await axios.post(`${state.apiUrl}/bookings/failed/delete`, payload, {headers: rootState.auth.headers});
      if (response.data.status == 200) {
        commit('removeEmail', payload.id);
        dispatch('global/setToastNotification', {title: i18n.t('lang.store.emails.emails'), text: i18n.t('lang.store.emails.emailDeleted'), type: 'success'}, {root: true});
      } else {
        const errorText = response.data.errors && response.data.errors.length ? response.data.errors[0] : i18n.t('lang.store.emails.errorDeletingEmail');
        dispatch('global/setToastNotification', {title: i18n.t('lang.store.emails.emails'), text: errorText, type: 'danger'}, {root: true});
      }
    } catch (error) {
      dispatch('global/setToastNotification', {title: i18n.t('lang.store.emails.emails'), text: i18n.t('lang.store.emails.errorDeletingEmail'), type: 'danger'}, {root: true});
    }
  },

  async getBookingById({commit, dispatch, state, rootState}, payload) {
    if (payload.id) {
      commit('setFailedEmail', payload);
      const targetIndex = state.targets.findIndex(tg => tg.toLowerCase() == payload.target);
      commit('setSelectedTarget', targetIndex);
    }
    const result = axios.post(`${state.apiUrlBooking}/bookings/booking`, {"bookingId": payload.booking_id}, {headers: rootState.auth.headers})
                        .then(async response => {
                          if (response.data.status == 200) {
                            commit('setContacts', {hotelContacts: response.data.data.hotelContacts, clientContacts: response.data.data.clientContacts});
                            // Resolve the tenant configuration first so the target options are final when the booking shows up.
                            await dispatch('getHostConfiguration', response.data.data.booking.parent_client_id);
                            commit('setBooking', response.data.data.booking);
                          } else {
                            dispatch('global/setToastNotification', {title:i18n.t('lang.store.emails.emails'), text:i18n.t('lang.store.emails.noBooking'), type:'danger'}, {root: true});
                          }
                        })
                        .catch(error => console.log(error));
  },

  // Tenant branding (logo, colors, support emails) used by the tenant mail template.
  async getHostConfiguration({commit, state}, parentId) {
    commit('setHostConfiguration', null);
    if (parentId) {
      try {
        const response = await axios.get(`${environmentService.apiUrlUser}/host/configuration`, {params: {parent_id: parentId}});
        commit('setHostConfiguration', response.data.status == 200 ? response.data.data : null);
      } catch (error) {
        console.log(error);
      }
    }
    // No tenant configuration: the selection can be at most 'Hotel & Client'.
    if (!state.hostConfiguration && tenantTargetIndexes.includes(state.selectedTarget)) {
      commit('setSelectedTarget', TARGET_HOTEL_CLIENT);
      commit('updateTarget');
    }
  },

  toggleNotify({commit}, payload) {
    commit('toggleNotify', payload);
  },

  addRecipient({commit}, payload) {
    commit('addRecipient', payload);
  },

  async sendEmail({commit, dispatch, state, rootState}, payload) {
    const data = {
      bookingId: state.booking.id,
      target: payload.target,
      clientContacts: state.clientContacts,
      hotelContacts: state.hotelContacts
    }

    await postRequestHandler(`${state.apiUrl}/bookings/resend`, data, {headers: rootState.auth.headers})
      .then(response => {
        dispatch('global/setToastNotification', {title:i18n.t('lang.store.emails.emails'), text:i18n.t('lang.store.emails.emailSent'), type:'success'}, {root: true});
        setTimeout(()=>{
          dispatch('getFailedMails');
          dispatch('global/setToastNotification', {title:i18n.t('lang.store.emails.emails'), text:i18n.t('lang.store.emails.listUpdate'), type:'success'}, {root: true});
        }, 10000)
        dispatch('cancelResend');
        dispatch('toggleSentDone', true);
      })
      .catch(error => console.log(error));
  },

  setSelectedTarget({ commit }, payload) {
    commit('setSelectedTarget', payload)
  },

  cancelResend({commit}) {
    commit('cancelResend');
  },

  async sendSupportEmail({commit, dispatch, state, rootState}, payload) {
    if (!state.apiUrl) {
      dispatch('setUrls');
    }
    const data = {
      email: payload.email,
      subject: payload.subject,
      message: payload.message,
      organization: payload.organization,
      type: payload.type,
      recaptchaToken: payload.recaptchaToken
    }

    await postRequestHandler(`${state.apiUrl}/support/send`, data, {headers: rootState.auth.headers})
      .then(response => {
        if(response.status == 200){
          dispatch('global/setToastNotification', {title:i18n.t('lang.store.emails.emails'), text:i18n.t('lang.store.auth.contact.emailSent'), type:'success'}, {root: true});
        } else {
          dispatch('global/setToastNotification', {title:i18n.t('lang.store.emails.emails'), text:i18n.t('lang.store.auth.contact.emailNotSent'), type:'danger'}, {root: true});
        }
      })
      .catch(error => {
        console.log(error);
        throw error;
      });
  }

};

export default {
  namespaced:true,
  state,getters,mutations,actions
};



// WEBPACK FOOTER //
// ./src/store/modules/mails.js